import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { generatePath, Link, useLocation, useNavigate, useParams } from "react-router-dom";
import KhanReportLogo from "components/shared/ReportComponents/KhanReortLogo";
import { useGetFeedbackStepDetailQuery, useSubmitReportMutation } from "store/services/clients/assessmentTreatmentPlan";
import { DISCHARGE_SCREENING_REPORT, DISCHARGE_SUMMARY_REPORT, SCHOOL_CLIENT_LIST } from "config/paths";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { CalendarIcon } from "Assets/icons";
import { dateFormat, getAge, getPdfBlob, successMessageToast, userFullName } from "helpers/common";
import { CLIENT_REPORT_TYPES, DISCHARGE_SCREENING_REPORT_QUESTIONS } from "config/constants";
import { RenderAnswerByQId } from "components/shared/ReportComponents/AnswerFields/Index";
import LoadingButton from "components/shared/LoadingButton";
import TherapistSignature from "components/shared/ReportComponents/TherapistSignature";
import logo from "../../../../../../Assets/images/logo.png";
import GuardianDetailRow from "components/shared/ReportComponents/GuardianDetailRow";
import HeaderPreview from "components/shared/ReportComponents/ReportHeader/Preview";
import { clientListingPath } from "helpers/navigationHelper";
import moment from "moment";
import { REPORT_SUBMITTED_SUCCESS } from "config/messages";
import { PDFViewer } from "@react-pdf/renderer";
import { DischargeScreeningPdfDoc } from 'components/PDF/Clients/Reports/DischargeScreeningPdf';
import { useUploadFileMutation } from "store/services/utility";
import { store } from "store/store";

const DischargeSummaryReportPreview = () => {

  const isVeteran = store.getState()?.auth?.user?.role_type === "veteran";

  const navigate = useNavigate();
  const { assessmentId, clientId, clientType } = useParams();

  const [isSubmitting, setSubmitting] = useState(false);

  const [submitReport, { isLoading: reportSubmitting }] = useSubmitReportMutation();

  const { data, isLoading, isFetching, isError, refetch } = useGetFeedbackStepDetailQuery({ assessmentId, clientId, clientType });
  const assessmentGoals = data?.assesmentData?.reportGoals;
  const assessmentAssignedId = data?.assesment?.id;
  const reportQuestions = data?.assesmentData?.reportQuestions;
  const reportAnswers = data?.assesmentData?.reportAnswers;

  const caseDiscipline = data?.clientPendingCaseload?.clientCaseloadTherapist?.caseDiscipline;
  const caseTherapist = data?.clientPendingCaseload?.clientCaseloadTherapist?.clientCaseloadMapping?.find(x => x.caseTherapist?.therapist_detail?.type == "veteran")?.caseTherapist;
  const therapistDetails = { caseDiscipline, caseTherapist }

  const [uploadFile, { isLoading: uploadingReportPdf }] = useUploadFileMutation();

  useEffect(() => {
    refetch();
  }, [refetch])

  const handleClickBack = () => {
    navigate(
      generatePath(DISCHARGE_SCREENING_REPORT, {
        assessmentId,
        clientId,
        clientType,
      })
    );
  }

  const handleSaveLater = async () => {
    successMessageToast("Record updated successfully.")
    navigate(clientListingPath(clientType));
  };

  const handleSubmitReport = async () => {
    try {
      setSubmitting(true);
      const doc = (
        <DischargeScreeningPdfDoc
          data={data}
        />
      )
      const filename = `Discharge Screening Report - ${caseDiscipline?.assign_term} - ${moment(new Date()).format("YYYY-MM-DD")}.pdf`
      let pdfFile = await getPdfBlob(doc, false, filename);

      let uploadedFileName = await uploadFile({ file: pdfFile, acl: "private", fileOriginalFormat: true }).unwrap();

      if (!uploadedFileName) {
        throw new Error("File not uploaded");
      };

      let payload = {
        assessmentAssignedId,
        data: {
          attachment_key: uploadedFileName,
          attachment_name: CLIENT_REPORT_TYPES[assessmentId].name,
        },
        clientType
      }

      await submitReport(payload).unwrap();

      setSubmitting(false);
      successMessageToast(REPORT_SUBMITTED_SUCCESS);
      navigate(clientListingPath(clientType));
    } catch (err) {
      setSubmitting(false);
      console.error("Error submitting report", err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <section className="ReportSec py-3 position-relative">
        <Container fluid>
          <Row>
            <Col lg="12" className="my-2">
              <div className="cardCstm p-lg-5 rounded-2 bg-white shadow">
                {isLoading ? (
                  <LoadingIndicator />
                ) : isError ? (
                  <ErrorComponent />
                ) : (
                  <>
                    {isFetching && <LoadingOverlay />}
                    <HeaderPreview
                      heading={data?.assesment?.report_heading}
                    />
                    <div className="formBody pt-3">
                      <div
                        className=""
                      // style={{ borderBottom: "2px dashed #6c6c6c" }}
                      >
                        <div className="py-3">
                          <div
                            className="cardCstm rounded-2 px-3"
                            style={{ background: "#F9F9F9" }}
                          >
                            <Row className="border-bottom p-2">
                              <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    Last Name
                                  </h6>
                                  <p className="m-0 themeBlue">
                                    {data?.lastname}
                                  </p>
                                </div>
                              </Col>
                              <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    First Name
                                  </h6>
                                  <p className="m-0 themeBlue">
                                    {data?.firstname}
                                  </p>
                                </div>
                              </Col>
                            </Row>
                            <Row className="border-bottom p-2">
                              <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    Date of Birth:
                                  </h6>
                                  <p className="m-0 themeBlue">
                                    <span className="icn me-2">
                                      <CalendarIcon />
                                    </span>
                                    {dateFormat(data?.client_detail?.dob)}
                                  </p>
                                </div>
                              </Col>
                              <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    Age:
                                  </h6>
                                  <p className="m-0 themeBlue">
                                    {getAge(data?.client_detail?.dob)}
                                  </p>
                                </div>
                              </Col>
                            </Row>
                            <Row className="border-bottom p-2">
                              <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    Clinician Name:
                                  </h6>
                                  <p className="m-0 themeBlue text-capitalize">{userFullName(therapistDetails?.caseTherapist)}</p>
                                </div>
                              </Col>
                              <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    Discipline Name:
                                  </h6>
                                  <p className="m-0 themeBlue">
                                    {therapistDetails?.caseDiscipline?.name}
                                  </p>
                                </div>
                              </Col>
                            </Row>
                            <Row className="border-bottom p-2">
                              <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    Screening Date:
                                  </h6>
                                  <p className="m-0 themeBlue">
                                    <span className="icn me-2">
                                      <CalendarIcon />
                                    </span>
                                    {dateFormat(moment())}
                                  </p>
                                </div>
                              </Col>
                              {/* <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    Teacher Name:
                                  </h6>
                                  <p className="m-0 themeBlue">John Wangdu</p>
                                </div>
                              </Col> */}
                            </Row>
                            <Row className="border-bottom p-2">
                              {clientType == "school" && <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    School Name:
                                  </h6>
                                  <p className="m-0 themeBlue">
                                    {data?.client_detail?.client_school_detail?.client_school?.name}
                                  </p>
                                </div>
                              </Col>}
                              <Col md="6" className="py-2">
                                <div className="d-flex align-items-start gap-10 flex-wrap">
                                  <h6
                                    className="m-0 fw-sbold themeBlue"
                                    style={{ minWidth: 200, fontSize: 14 }}
                                  >
                                    City/Town:
                                  </h6>
                                  <p className="m-0 themeBlue">
                                    {data?.client_detail?.city_town}
                                  </p>
                                </div>
                              </Col>
                            </Row>
                            {/* Guardian Details */}
                            {data?.client_detail?.client_guardian_mapping?.length > 0 &&
                              data?.client_detail?.client_guardian_mapping?.map(
                                (guardian, index) => {
                                  const slug = guardian?.client_guardian?.client_role?.slug;
                                  if (slug !== (clientType == "school" ? "teacher" : "parent")) {
                                    return null;
                                  }

                                  return (
                                    <GuardianDetailRow
                                      index={index}
                                      guardian={guardian}
                                      slug={slug}
                                    />
                                  );
                                }
                              )}
                          </div>
                        </div>
                        <RenderAnswerByQId
                          questionId={DISCHARGE_SCREENING_REPORT_QUESTIONS.screening_results}
                          reportQuestions={reportQuestions}
                          reportAnswers={reportAnswers}
                        />
                        <RenderAnswerByQId
                          questionId={DISCHARGE_SCREENING_REPORT_QUESTIONS.greeting}
                          reportQuestions={reportQuestions}
                          reportAnswers={reportAnswers}
                          showQuestion={false}
                        />
                      </div>
                      <div className="py-3">
                        <TherapistSignature
                          therapistDetails={therapistDetails}
                        />
                      </div>
                      <div className="py-3">
                        <KhanReportLogo />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Col>
            <Col lg="12" className="my-2 d-flex gap-10">
              <Button
                style={{
                  background: "rgb(69, 163, 165)",
                  borderColor: "rgb(69, 163, 165)",
                }}
                onClick={handleClickBack}
                disabled={!!isSubmitting}
                className="d-flex align-items-center justify-content-center commonBtn fw-sbold"
              >
                Back
              </Button>
              {
                isVeteran && <Button
                  style={{
                    background: "rgb(69, 163, 165)",
                    borderColor: "rgb(69, 163, 165)",
                  }}
                  disabled={!!isSubmitting}
                  onClick={handleSaveLater}
                  className="d-flex align-items-center justify-content-center commonBtn fw-sbold"
                >
                  Save For Later
                </Button>
              }
              {
                isVeteran && <LoadingButton
                  className="d-flex align-items-center justify-content-center commonBtn"
                  onClick={handleSubmitReport}
                  isLoading={isSubmitting}
                >
                  Submit
                </LoadingButton>
              }
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default DischargeSummaryReportPreview;
